import { useId, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogCancel } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Alert } from '@/components/ui/Alert';
import { ApiErrorAlert } from '@/components/ui/ApiErrorAlert';
import { GrantForm } from './GrantForm';
import { expiresAtToIso, resolveGrant, type GrantFormState } from './grant-state';
import { EMPTY_SCOPE_SELECTION, scopeSelectionGap } from './scope-selection';
import { previewGrant, type HierarchySnapshot } from './effective-permissions';
import { inviteTechnician } from '@/lib/api/endpoints/technicians';
import { listRoles } from '@/lib/api/endpoints/technicians';
import { listDeviceGroups, listOrganizations, listSites } from '@/lib/api/endpoints/organizations';
import { listDevices } from '@/lib/api/endpoints/devices';
import { queryKeys } from '@/lib/query-keys';
import { mergeFieldErrors, type LocalFieldErrors } from '@/lib/form-errors';
import type { RoleAssignment, TechnicianInviteResult } from '@/lib/api/types';

/*
  Invite a technician: identity, then one role at one scope.

  The invitation carries no credential. The server emails a single-use link to the address typed
  here and the response deliberately does not contain it, so an administrator who can read this
  dialog's result still cannot sign in as the person they invited. The dialog says this rather
  than leaving the technician wondering where the password is.

  One assignment at invite time, more added afterwards from the person's row. That is not a
  limitation worth working around: the first grant is the one that decides what the account is
  for, and a form that collected four of them would bury the no-escalation preview under a list.

  The invite needs technician.manage, and the grant additionally has to pass the no-escalation
  rule from docs/07-identity-rbac.md I.2. Both are enforced server side. The button is disabled
  when the local copy of the escalation check says the grant is impossible, with the blocked
  permissions named in the preview panel, so the refusal is legible before the round trip rather
  than arriving as a 403 about permissions the user never saw.

  The device list is fetched with a high limit because the scope picker needs it to offer a
  single device and to count what a scope reaches. It is a separate query key from the device
  page's, so it does not disturb the table's paging.
*/

/** Enough to cover the fixture fleet and a small real one. Paging a picker is Phase 2 work. */
const SCOPE_DEVICE_LIMIT = 500;

export interface InviteTechnicianDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The signed-in user's own assignments, for the no-escalation check. */
  readonly grantorAssignments: readonly RoleAssignment[];
}

export function InviteTechnicianDialog({
  open,
  onOpenChange,
  grantorAssignments,
}: InviteTechnicianDialogProps): React.JSX.Element {
  const formId = `invite-${useId()}`;
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [grant, setGrant] = useState<GrantFormState>({
    roleId: null,
    scope: EMPTY_SCOPE_SELECTION,
    expiresAtLocal: '',
  });
  const [local, setLocal] = useState<LocalFieldErrors>({});
  const [result, setResult] = useState<TechnicianInviteResult | null>(null);

  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setEmail('');
      setFullName('');
      setGrant({ roleId: null, scope: EMPTY_SCOPE_SELECTION, expiresAtLocal: '' });
      setLocal({});
      setResult(null);
    }
  }

  const roles = useQuery({ queryKey: queryKeys.roles, queryFn: listRoles });
  const organizations = useQuery({
    queryKey: queryKeys.organizations,
    queryFn: listOrganizations,
  });
  const sites = useQuery({ queryKey: queryKeys.sites(), queryFn: () => listSites() });
  const groups = useQuery({
    queryKey: queryKeys.deviceGroups(),
    queryFn: () => listDeviceGroups(),
  });
  const devices = useQuery({
    queryKey: queryKeys.devices({ limit: SCOPE_DEVICE_LIMIT }),
    queryFn: () => listDevices({ limit: SCOPE_DEVICE_LIMIT }),
  });

  const hierarchy: HierarchySnapshot = useMemo(
    () => ({
      sites: sites.data ?? [],
      groups: groups.data ?? [],
      devices: devices.data?.items ?? [],
    }),
    [sites.data, groups.data, devices.data],
  );

  const allRoles = useMemo(() => roles.data ?? [], [roles.data]);
  const loading =
    roles.isPending || organizations.isPending || sites.isPending || groups.isPending || devices.isPending;

  const mutation = useMutation({
    mutationFn: async (): Promise<TechnicianInviteResult> => {
      const resolved = resolveGrant(grant, allRoles);
      if (resolved === null) {
        throw new Error('The role and scope are not complete.');
      }
      return inviteTechnician({
        email: email.trim().toLowerCase(),
        fullName: fullName.trim(),
        roleId: resolved.role.id,
        scope: resolved.scope,
        expiresAt: expiresAtToIso(grant.expiresAtLocal),
      });
    },
    onSuccess: (invited) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.technicians });
      void queryClient.invalidateQueries({ queryKey: queryKeys.auditEvents('') });
      // The dialog stays open to show the confirmation, because "we sent a link, here is when it
      // expires" is information the administrator needs and a toast would take it away again.
      setResult(invited);
    },
  });

  const errors = mergeFieldErrors(local, mutation.error);
  const resolved = resolveGrant(grant, allRoles);
  const preview =
    resolved === null
      ? null
      : previewGrant(resolved.role, resolved.scope, grantorAssignments, allRoles, hierarchy);
  const escalation = preview !== null && preview.blocked.length > 0;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (email.trim() === '') {
      found.email = 'An email address is required. The invitation link is sent there.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      found.email = 'That does not look like an email address.';
    }
    if (fullName.trim() === '') {
      found.fullName = 'A name is required, so audit entries name a person rather than an address.';
    }
    if (grant.roleId === null) {
      found.roleId = 'Choose a role.';
    }
    const gap = scopeSelectionGap(grant.scope);
    if (gap !== null) {
      found.scope = gap;
    }
    const iso = expiresAtToIso(grant.expiresAtLocal);
    if (grant.expiresAtLocal !== '' && iso === null) {
      found.expiresAt = 'That is not a valid date and time.';
    } else if (iso !== null && Date.parse(iso) <= Date.now()) {
      found.expiresAt = 'An expiry in the past would grant nothing. Leave it empty for no expiry.';
    }

    setLocal(found);
    if (Object.keys(found).length > 0 || escalation) {
      return;
    }
    mutation.mutate();
  }

  if (result !== null) {
    return (
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        title={`Invitation sent to ${result.technician.fullName}`}
        description="The account exists and is waiting for the invitee to accept."
        width="sm"
        footer={
          <Button
            variant="primary"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Done
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          <Alert tone="success" title="Invited">
            <p>
              A single-use link has gone to{' '}
              <span className="font-mono">{result.technician.email}</span>. It expires{' '}
              {new Date(result.invitationExpiresAt).toLocaleString()}.
            </p>
            <p className="mt-1">
              The link is not shown here and is not in the API response, so nobody with access to
              this console can use it to sign in as them.
            </p>
          </Alert>
          <p className="text-xs text-fg-muted">
            They will be required to set a password and enroll a second factor before the account
            becomes active. Until then their status is <span className="font-semibold">invited</span>{' '}
            and the assignment you granted confers nothing, because there is no way to authenticate
            as them yet.
          </p>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Invite a technician"
      description="Creates an account in this tenant and emails a single-use invitation. Requires technician.manage."
      width="lg"
      busy={mutation.isPending}
      footer={
        <>
          <DialogCancel disabled={mutation.isPending} />
          <Button
            type="submit"
            form={formId}
            variant="primary"
            loading={mutation.isPending}
            disabled={escalation || loading}
            title={
              escalation
                ? 'This grant includes permissions you do not hold at that scope. See the list above.'
                : undefined
            }
          >
            Send invitation
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-5">
        <section className="flex flex-col gap-4">
          <h3 className="text-xs font-semibold tracking-wide text-fg-muted uppercase">Person</h3>
          <Field
            type="email"
            label="Email address"
            required
            autoFocus
            value={email}
            maxLength={200}
            autoComplete="off"
            hint="The invitation goes here, and this is the address they sign in with."
            onChange={(event) => {
              setEmail(event.target.value);
            }}
            {...(errors.forField('email') === undefined ? {} : { error: errors.forField('email') })}
          />
          <Field
            label="Full name"
            required
            value={fullName}
            maxLength={120}
            autoComplete="off"
            hint="Shown on every audit entry and in every session record they create."
            onChange={(event) => {
              setFullName(event.target.value);
            }}
            {...(errors.forField('fullName') === undefined
              ? {}
              : { error: errors.forField('fullName') })}
          />
          <Alert tone="info" title="Second factor is mandatory">
            This tenant enforces MFA, and any role holding a dangerous permission requires it
            regardless. They will enroll an authenticator before the account activates; there is no
            opt-out to grant them.
          </Alert>
        </section>

        <hr className="border-border" />

        <section className="flex flex-col gap-4">
          <h3 className="text-xs font-semibold tracking-wide text-fg-muted uppercase">
            First role assignment
          </h3>
          <GrantForm
            state={grant}
            onChange={setGrant}
            subject={fullName.trim() === '' ? 'This person' : fullName.trim()}
            roles={allRoles}
            organizations={organizations.data ?? []}
            hierarchy={hierarchy}
            grantorAssignments={grantorAssignments}
            loading={loading}
            {...(errors.forField('roleId') === undefined
              ? {}
              : { roleError: errors.forField('roleId') })}
            {...(errors.forField('scope') === undefined
              ? {}
              : { scopeError: errors.forField('scope') })}
            {...(errors.forField('expiresAt') === undefined
              ? {}
              : { expiresAtError: errors.forField('expiresAt') })}
          />
        </section>

        {mutation.isError && !errors.allErrorsAreFieldLevel ? (
          <ApiErrorAlert error={mutation.error} context="Could not send the invitation" />
        ) : null}
      </form>
    </Dialog>
  );
}
